import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import App from "./App.js";
import { useBentoStore } from "./store/index.js";
import { applyTheme } from "./store/theme.js";
import { bentoMount } from "./shell-bridge.js";
import { startPlacing, isPlacing, cancelPlacing } from "./mobile/engine.js";

// --- Store bridge (M1 Phase 2) ---
// The legacy app runs as classic <script>s and can't import ES modules, so we
// expose the store on window for it to read/write. Both the legacy code and
// React share this one store during the migration; the bridge goes away once
// everything is React (Phase 3+).
window.bentoStore = useBentoStore;

// React → legacy shell helper (Phase 4, PR 9): flushSync-backed sync add so the
// desktop createModule can return the freshly-rendered <PlacedModule>, plus two
// pure control-config lookups `buildModuleData` seeds new modules with (moved
// here in PR 11 when the `bentoReact`/`bentoControls` mount bridges — which had
// no live callers once `<PlacedModule>` started composing `<ModuleBody>`/
// `<ModuleControls>` directly — were removed). The reverse seam — legacy
// capabilities React calls — is `window.bentoShell`, set up by main.js. See
// shell-bridge.ts.
window.bentoMount = bentoMount;
// The phone drag engine's tray hooks (main.js's bottom sheet calls these).
window.bentoMobile = { startPlacing, isPlacing, cancelPlacing };

// The store is the source of truth for the theme; this applies changes to the
// document. Keeping the side effect here (not in the store) leaves the store
// pure. Initial paint is handled by the anti-flash inline script in index.html,
// so we react to changes only.
useBentoStore.subscribe((state, prev) => {
  if (state.theme !== prev.theme) applyTheme(state.theme);
});

// React Query client (M2 slice 4). Widgets fetch their data from app-api
// through `useWidgetData`; this provides the cache. The dummy data is static,
// so treat fetched data as fresh for a while and don't refetch on focus — the
// only trigger for a new fetch is the fixture (part of the query key) changing.
// `retry` keeps the default so a transient failure recovers on its own; the
// widget's error state also offers a manual retry.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});

// Mount the React root. It lives at #react-root — a dedicated node the legacy
// app never touches — so mounting is purely additive and cannot disturb the
// existing DOM. The QueryClientProvider wraps <App /> so every widget body
// (portalled into #workspace by <Workspace>) inherits the same query cache.
const root = document.getElementById("react-root");
if (root) {
  createRoot(root).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </StrictMode>
  );
}
